/**
 * The shape of the welcome, as data rather than as a number in a template.
 *
 * `OnboardingPage.vue` used to hold its order in a bare `step` integer and a
 * `TITLES` map keyed by that integer, which meant the ORDER of the questions
 * was a fact nothing could check. Moving the alerts step from third to fourth
 * was five edits across a template, and the one that mattered — that a
 * permission is asked after the person has seen what the app does, not before
 * — was not written down anywhere a test could read it.
 *
 * So the order lives here, the page reads it, and the suite asserts the two
 * rules that are actually load-bearing:
 *
 *   BOTH PERMISSIONS ARE ASKED TOGETHER, on one step, and that step is the
 *   last question before the finish. Notifications and location used to sit on
 *   either side of the home-and-work step: a person was asked for a system
 *   permission before the app had shown them a single reason to say yes.
 *
 *   EVERY STEP EXCEPT THE FIRST IS OPTIONAL. Stations are the one answer the
 *   app cannot work without — there is nothing to watch otherwise — and every
 *   other question improves the app rather than enabling it.
 *
 * Pure, and imported by the page rather than the other way round, so none of
 * this drags a component into the test runner.
 */

import { modeForStep, welcomeModeSteps } from './modes.js';

/**
 * The questions, in the order they are asked.
 *
 * A MODE'S STEP is its own step STRAIGHT AFTER THE STATIONS, and the stations
 * step is exactly what it was. Somebody who has just named Carnegie is thinking
 * about how they get about, which is the moment "and any trams?" is a natural
 * second question rather than a detour; asked on the stations step it would be a
 * second list under the first, and asked later it would be a question about a
 * commute the person has already moved on from. Only in a city that declares the
 * mode — see `offersMode` — and optional, with "No trams" as the way past.
 *
 * WHICH STEPS THEY ARE IS THE REGISTRY'S (src/modes.js `welcome.step`): the
 * steps are saved by name in `lateagain:welcome-step`, so Melbourne's stays
 * `trams` and an interrupted welcome resumes exactly where it was.
 */
export const welcomeSteps = () => ['stations', ...welcomeModeSteps(), 'places', 'what', 'plan', 'permissions', 'done'];

/** The order for the registry as committed. The page reads this. */
export const WELCOME_STEPS = welcomeSteps();

/** The steps a person may walk past without answering. */
export const optionalSteps = () => [...welcomeModeSteps(), 'places', 'plan', 'permissions'];
export const OPTIONAL_STEPS = optionalSteps();

/** Does this city have this mode step to ask about? */
export function offersMode(step, { city = null } = {}) {
  return Boolean(city) && modeForStep(city, step) !== null;
}

/**
 * Is the offer worth a screen on this phone? Only where there is something to
 * sell and the person has not already bought it: not on the web or in the demo
 * (no store, nothing is gated), and not for a phone that is already
 * subscribed. The step is then not shown, and the stamp does not count it.
 */
export function offersPlan({ available = false, entitled = false } = {}) {
  return available === true && entitled !== true;
}

/**
 * The steps this phone will actually see, in order. A mode's step is asked only
 * where `opts.city` declares the mode; a caller that says nothing about the city
 * is a caller that sees the welcome as it was before there were modes.
 */
export function visibleSteps(opts = {}) {
  const modeSteps = new Set(welcomeModeSteps());
  return welcomeSteps()
    .filter((id) => id !== 'plan' || offersPlan(opts))
    .filter((id) => !modeSteps.has(id) || offersMode(id, opts));
}

/** How many questions the stamp counts. */
export const STEP_COUNT = WELCOME_STEPS.length;

/** The step id at a one-based position, clamped to something real. */
export function stepAt(n) {
  const i = Math.trunc(Number(n));
  const steps = welcomeSteps();
  return steps[i - 1] ?? steps[0];
}

/** The one-based position of a step id, or 1 for anything unknown. */
export function stepNumber(id) {
  const i = welcomeSteps().indexOf(id);
  return i < 0 ? 1 : i + 1;
}

/** Can this step be walked past without answering it? */
export function isOptional(id) {
  return optionalSteps().includes(id);
}

/**
 * The order the welcome had before the trams step, which is the order every
 * resume point saved by an older build is counted in. Frozen: it is a record
 * of what was stored, not a list to keep up to date.
 */
const LEGACY_STEPS = ['stations', 'places', 'what', 'plan', 'permissions', 'done'];

/**
 * Where an interrupted welcome starts again.
 *
 * The welcome is one page holding a step, so anything that reloads the
 * WebView — an over-the-air bundle applied on resume, a crash, a pulled-down
 * refresh on the web — used to put the person back on step one with the
 * permission they had just answered forgotten. `main.js` already refuses to
 * apply an update while the welcome is on screen, which covers the OTA case;
 * this covers every other one, and costs one short string in prefs.
 *
 * THE STEP IS SAVED BY NAME. It used to be saved as a position, and a
 * position means a different question every time a step is added: a person
 * interrupted on "Where are home and work?" (2) by the update that added
 * "Any trams?" would have come back to trams, and one on the permissions (5)
 * to the plan. A number found in prefs is therefore one an older build wrote,
 * and it is read in that build's order, `LEGACY_STEPS`.
 *
 * It NEVER resumes onto the finish. Landing a cold start on "That's it." with
 * no memory of what was answered is a screen saying the setup is done to
 * somebody who has not seen a question, so the last resumable step is the last
 * one that asks something.
 */
export function resumeStep(saved) {
  const last = welcomeSteps().length - 1;
  if (typeof saved === 'string' && welcomeSteps().includes(saved)) {
    return Math.min(stepNumber(saved), last);
  }
  if (typeof saved === 'string' && saved.trim() === '') return 1;
  const n = Math.trunc(Number(saved));
  if (saved === null || !Number.isFinite(n) || n < 1) return 1;
  const id = LEGACY_STEPS[n - 1];
  return id ? Math.min(stepNumber(id), last) : last;
}

/** Was a welcome begun and not finished? Anything past the first step. */
export function welcomeBegun(saved) {
  return resumeStep(saved) > 1;
}

/**
 * What the welcome's alerts button says. A state, not a command, once it has
 * been pressed; before that, the offer.
 *
 * "· 7 DAYS FREE" ONLY WHEN THE STORE SAYS SO. `trialDays` is the store's
 * answer for this account (0 while it has not answered, where nothing is sold,
 * and for somebody who has had the trial before). Naming a free week the
 * store will then charge for is the one thing this button must never say. The
 * wall itself still opens only on the tap.
 */
export function alertsButtonLabel({ state = null, pushEnabled = false, trialDays = 0 } = {}) {
  if (state === 'asking') return 'Asking…';
  if (state === 'on' || pushEnabled) return 'Alerts are on';
  if (state) return 'Try again';
  if (trialDays > 0) return `Turn alerts on · ${trialDays} ${trialDays === 1 ? 'day' : 'days'} free`;
  return 'Turn alerts on';
}

/**
 * What to say about a location attempt that did not produce a station.
 *
 * ONE SENTENCE PER OUTCOME, because the outcomes are genuinely different and
 * the screen used to print "The phone said no" for all of them. On the web
 * that is very nearly always a lie: `navigator.geolocation` calls NEITHER
 * callback while its permission bubble is open, so a person who has not
 * answered yet, a person who dismissed the bubble with its close button, and a
 * person on an insecure origin all arrive here the same way — through the
 * timeout — and none of them refused anything.
 *
 * `native` picks the words for where the answer is changed: a phone has an OS
 * settings screen, a browser has a control in its address bar.
 */
export function locationSays(reason, { native = false } = {}) {
  const retry = native
    ? 'The board still works by search, and the station picker can ask again later.'
    : 'The board still works by search. To change it, allow location for this site from the icon in the address bar.';

  switch (reason) {
    case 'denied':
      return `${native ? 'The phone said no.' : 'The browser said no.'} ${retry}`;
    case 'timeout':
      return `Nothing came back. The permission box may still be open, or it was closed without an answer. ${retry}`;
    case 'insecure':
      return 'This page is not on a secure connection, so the browser will not hand over a position at all. Open Late Again over https and this works.';
    case 'unsupported':
      return `This browser has no location at all. ${retry}`;
    case 'unavailable':
      return `Location is not available in this build. ${retry}`;
    case 'nowhere':
      return 'Found you, but there is no station of this network within reach. The board still works by search.';
    default:
      return `The location could not be read. ${retry}`;
  }
}
